// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import api from './advisorApi';

/** Upload progress callback payload (mirrors {@code policyDocumentApi}). */
export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

/**
 * A document a client uploaded and linked to a policy, as seen by the advisor.
 * Mirrors {@code com.loa.dto.clientdocument.ClientDocumentInfo}.
 */
export interface ClientUploadedDocumentDTO {
  id: string;
  documentName: string;
  policyId?: string | null;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  checksum: string;
  /** Internal tenant storage path — used to download via the signed-document download-by-path endpoint. */
  storagePath: string;
  uploadedAt: string;
  uploadedBy: string;
}

/** Result of an advisor relationship upload. Mirrors {@code ClientDocumentUploadResult}. */
export interface ClientDocumentUploadResult {
  id: string;
  profileId: string;
  policyId?: string | null;
  documentName: string;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
  checksum: string;
  storagePath: string;
  uploadedAt: string;
  uploadedBy: string;
}

/**
 * Advisor-facing read client for documents clients uploaded and linked to a
 * policy. Backs the policy detail "Documents" tab. Downloads go by id through
 * {@code /client-documents/{id}/download}, which preserves the file's real
 * content type (uploads may be PDF, image, Word or Excel — unlike LOAs/CoAs
 * which are always PDF, so the generic signed-document download must not be
 * reused for these).
 */
export const clientDocumentApi = {
  listByPolicy: async (policyId: string): Promise<ClientUploadedDocumentDTO[]> => {
    const { data } = await api.get<ClientUploadedDocumentDTO[]>(
      `/client-documents/policy/${policyId}`,
    );
    return data;
  },

  /**
   * Advisor upload of a document attached to the relationship (profile) rather
   * than to a policy. The document is hidden from the client until the advisor
   * turns the "Show to client" toggle on. {@code documentType} is the required
   * business classification ({@code RelationshipDocumentType} name).
   */
  uploadToRelationship: async (
    profileId: string,
    file: File,
    documentName: string,
    documentType: string,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<ClientDocumentUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentName', documentName);
    formData.append('documentType', documentType);

    const response = await api.post<ClientDocumentUploadResult>(
      `/client-documents/profile/${profileId}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            onProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total),
            });
          }
        },
      },
    );
    return response.data;
  },

  /** Toggle whether an advisor-uploaded relationship document is shown to the client. */
  setVisibility: async (id: string, visible: boolean): Promise<void> => {
    await api.patch(`/client-documents/${id}/visibility`, { visible });
  },

  /** Soft-delete an advisor-uploaded relationship document. */
  deleteDocument: async (id: string): Promise<void> => {
    await api.delete(`/client-documents/${id}`);
  },

  /**
   * Downloads as a Blob (with the document's real content type) and parses the
   * original filename out of {@code Content-Disposition}.
   */
  download: async (id: string): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get<Blob>(`/client-documents/${id}/download`, {
      responseType: 'blob',
    });
    const contentDisposition = response.headers['content-disposition'] as string | undefined;
    let filename = 'document';
    if (contentDisposition) {
      const match = /filename="?([^"]+)"?/.exec(contentDisposition);
      if (match) filename = match[1];
    }
    return { blob: response.data, filename };
  },
};
